// @flow
import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useHistory, useLocation} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';
import {
  Typography,
  Hidden,
  IconButton,
  LinearProgress,
  CircularProgress,
} from '@material-ui/core';
import {ArrowBack} from '@material-ui/icons';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import imagesConfirm from '../../../assets/img/imagesConfirm.svg';

import type {Store, User} from '../../../entities';
import CustomExitPrompt from '../../../elements/CustomExitPrompt/CustomExitPrompt';
import {usePage} from '../../page/utils';

import {connect} from 'react-redux';
import type {RootState} from '../../index';
import type {Dispatch} from 'redux';
import {bindActionCreators} from 'redux';
import {actions as ProductsActions} from '../duck';
import InstagramImage from './InstagramProductsUpload/InstagramImage';
import type {InstagramImageType} from './InstagramProductsUpload/InstagramImage';
import queryString from 'query-string';
import CustomModal from '../../../elements/CustomModal/CustomModal';
import {scrollTop} from '../../../utils/dom';
import Product from '../containers/ProductContainer';

type Props = {|
  store?: Store,
  user?: User,
  createProductSuccess?: string | boolean,
  resetProductState: () => void,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#F8F9FB',
  },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  dividerVertical: {
    margin: theme.spacing(0, 1),
    background: colors.grey300,
  },
  headerWrapper: {
    maxWidth: 1300,
    padding: theme.spacing(4, 4, 0, 4),
    background: '#f8f9fb',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 4, 0, 4),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(3, 2, 0, 2),
      },
    },
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  textRow: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    justifyContent: 'space-between',
  },
  descriptionText: {
    color: colors.grey900,
    fontWeight: 600,
  },
  selectedCountText: {
    color: colors.grey700,
    fontWeight: 500,
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
      alignSelf: 'center',
      // padding: theme.spacing(1, 2, 0, 2),
      color: colors.grey900,
      fontWeight: 600,
    },
  },
  instagramImagesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    margin: `0 ${theme.spacing(-3)} ${theme.spacing(-3)} 0`,
  },
  instagramImage: {maxHeight: 200, objectFit: 'contain'},
  loadMoreButton: {
    marginTop: theme.spacing(3),
    borderRadius: '12px',
    background: colors.lightPurple,
  },
  subTitle: {
    padding: theme.spacing(1, 4, 0, 4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 2, 0, 2),
    },
    color: colors.grey900,
    fontWeight: 600,
  },
  progressBackground: {
    zIndex: 2,
    // position: 'sticky',
    // top: 88,
    margin: theme.spacing(1, 4, 0, 4),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1, 4, 0, 4),
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(1, 2, 0, 2),
      },
    },
    background: colors.grey300,
    borderRadius: 8,
    height: 36,
    padding: `14px ${theme.spacing(4)}`,
    [theme.breakpoints.down('xs')]: {
      padding: `14px ${theme.spacing(2)}`,
    },
    display: 'flex',
    alignItems: 'center',
  },
  progressBarRoot: {
    width: '100%',
    height: 8,
    borderRadius: 4,
  },
  progressBarColorPrimary: {
    backgroundColor: colors.grey400,
  },
  progressBarBar: {
    borderRadius: 4,
  },
  progressBarText: {
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      marginLeft: theme.spacing(2),
    },
    color: colors.grey900,
    fontSize: '0.875rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  earlyExitModal: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 500,
    padding: theme.spacing(4, 0),
  },
  earlyExitModalTitle: {
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '1.5rem',
    color: colors.grey800,
  },
  earlyExitModalText: {
    textAlign: 'center',
    margin: theme.spacing(3, 5),
    fontWeight: 480,
    color: colors.grey800,
  },
  earlyExitModalConfirmButton: {
    height: 48,
    width: 260,
    fontSize: '1rem',
  },
  earlyExitModalCancelButton: {
    marginTop: theme.spacing(1),
    height: 48,
    width: 260,
    fontSize: '1rem',
  },
  successModal: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 400,
    padding: theme.spacing(5, 8),
  },
  successModalText: {
    textAlign: 'center',
    margin: theme.spacing(3),
    fontWeight: 600,
    fontSize: '1.5rem',
    color: colors.grey900,
  },
  successModalButton: {
    height: 48,
    width: 260,
    fontSize: '1rem',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1300,
    marginBottom: 80,
    // overflowY: 'auto',
    // height: '100%',
    // background: '#f8f9fb',
    // overflow: 'auto',
    padding: theme.spacing(0, 4, 4, 4),
    [theme.breakpoints.only('sm')]: {
      marginBottom: 144,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 2, 2),
      marginBottom: 180,
    },
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    // alignItems: 'center',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(0, 3, 3, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 2, 2),
    },
    background: 'white',
  },
  bottomBar: {
    zIndex: 1200, // same as sidebar
    position: 'fixed',
    bottom: 0,
    width: 'calc(100% - 264px)',
    backgroundColor: 'white',
    boxShadow: '0px -4px 12px rgba(222, 222, 222, 0.25)',
    [theme.breakpoints.down('sm')]: {
      bottom: 64,
      width: '100%',
    },
    [theme.breakpoints.down('xs')]: {
      bottom: 64,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      // alignItems: 'center',
    },
  },
  bottomBarContent: {
    maxWidth: 1300,
    padding: theme.spacing(2, 4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bottomBarText: {
    fontWeight: 600,
  },
  bottomBarButton: {
    height: 48,
    fontSize: '1.125rem',
  },
  mobileBottomButton: {
    zIndex: 1,
    margin: theme.spacing(2),
    width: 'calc(100% - 32px)',
    fontSize: '1.125rem',
  },
  rootModal: {
    zIndex: '1400 !important',
  },
}));

const InstagramProductsUploadPage = usePage<Props>(
  InstagramProductsUpload,
  'Instagram Products Upload'
);

function InstagramProductsUpload(props: Props) {
  const {user, createProductSuccess, resetProductState} = props;

  const classes = useStyles();

  const location = useLocation();
  const queryObject = queryString.parse(location.search);

  // const mobile = useMediaQuery('(max-width:600px)');

  const history = useHistory();

  const [instagramMedia, setInstagramMedia] = useState([]);
  const [instagramPaging, setInstagramPaging] = useState();
  const [instagramLoading, setInstagramLoading] = useState(true);
  const [instagramError, setInstagramError] = useState();

  const getInstagramValidMedia = mediaArray => {
    const result = [];
    mediaArray.forEach(media => {
      if (media.media_type === 'IMAGE' || media.media_type === 'CAROUSEL_ALBUM')
        result.push(media);
    });
    return result;
  };

  useEffect(() => {
    if (
      queryObject.user_id &&
      typeof queryObject.user_id === 'string' &&
      queryObject.access_token &&
      typeof queryObject.access_token === 'string'
    ) {
      fetch(
        `https://graph.instagram.com/v21.0/${queryObject.user_id}/media?fields=id,caption,media_type,media_url,permalink,thumbnail_url,username,timestamp&access_token=${queryObject.access_token}`
      )
        .then(response => response.json())
        .then(data => {
          setInstagramLoading(false);
          if (data.data) {
            setInstagramMedia(getInstagramValidMedia(data.data));
            // setInstagramMedia([]);
            setInstagramPaging(data.paging);
          } else if (data.error) {
            setInstagramError(data.error.message);
            // console.error('then', data.error);
            // history.push(`/products?instagramUpload=1`);
          }
        })
        .catch(error => {
          setInstagramLoading(false);
          setInstagramError(error.message);
          // console.error('catch', error);
          // history.push(
          //   `/products?instagramUpload?=1`
          // );
        });
    } else if (
      queryObject.error_message &&
      typeof queryObject.error_message === 'string'
    ) {
      setInstagramError(queryObject.error_message);
      // history.goBack();
    } else {
      history.push(`/products`);
    }
  }, [
    history,
    queryObject.access_token,
    queryObject.error_message,
    queryObject.user_i,
    queryObject.user_id,
  ]);

  const loadMore = () => {
    if (instagramPaging && instagramPaging.next) {
      setInstagramLoading(true);
      fetch(instagramPaging.next)
        .then(response => response.json())
        .then(data => {
          setInstagramLoading(false);
          if (data.data) {
            setInstagramMedia(prev => [
              ...prev,
              ...getInstagramValidMedia(data.data),
            ]);
            setInstagramPaging(data.paging);
          } else if (data.error) {
            setInstagramError(data.error.message);
            // console.error('then', data.error);
            // history.push(`/products?instagramUpload=1`);
          }
        })
        .catch(error => {
          setInstagramLoading(false);
          setInstagramError(error.message);
          // console.error('catch', error);
          // history.push(
          //   `/products?instagramUpload?=1`
          // );
        });
    }
  };

  const [selectedInstagramMedia, setSelectedInstagramMedia] = useState([]);

  const handleImageSelect = (image: InstagramImageType) => {
    const aux = [...selectedInstagramMedia];
    const auxIndex = aux.findIndex(i => i.id === image.id);
    if (auxIndex > -1) {
      aux.splice(auxIndex, 1);
    } else {
      aux.push(image);
    }
    setSelectedInstagramMedia(aux);
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(-1);
  const [productsCreated, setProductsCreated] = useState(0);

  const [defaultImages, setDefaultImages] = useState([]);

  const getInstagramValidMediaUrls = mediaArray => {
    const result = [];
    mediaArray.forEach(media => {
      if (media.media_type === 'IMAGE' || media.media_type === 'CAROUSEL_ALBUM')
        result.push(media.media_url);
    });
    return result;
  };

  useEffect(() => {
    if (selectedInstagramMedia[currentImageIndex]) {
      if (selectedInstagramMedia[currentImageIndex].media_type === 'IMAGE')
        setDefaultImages([selectedInstagramMedia[currentImageIndex].media_url]);
      else if (
        selectedInstagramMedia[currentImageIndex].media_type ===
        'CAROUSEL_ALBUM'
      ) {
        fetch(
          `https://graph.instagram.com/v21.0/${
            selectedInstagramMedia[currentImageIndex].id
          }/children?fields=id,media_type,media_url,permalink,thumbnail_url,username,timestamp&access_token=${
            typeof queryObject.access_token === 'string'
              ? queryObject.access_token
              : ''
          }`
        )
          .then(response => response.json())
          .then(data => {
            // setInstagramLoading(false);
            if (data.data) {
              setDefaultImages(getInstagramValidMediaUrls(data.data));
              // setInstagramMedia([]);
              // setInstagramPaging(data.paging);
            } else if (data.error) {
              setInstagramError(data.error.message);
              setDefaultImages([
                selectedInstagramMedia[currentImageIndex].media_url,
              ]);
              // console.error('then', data.error);
              // history.push(`/products?instagramUpload=1`);
            }
          })
          .catch(error => {
            // setInstagramLoading(false);
            setInstagramError(error.message);
            setDefaultImages([
              selectedInstagramMedia[currentImageIndex].media_url,
            ]);
            // console.error('catch', error);
            // history.push(
            //   `/products?instagramUpload?=1`
            // );
          });
      }
    }
  }, [currentImageIndex, queryObject.access_token, selectedInstagramMedia]);

  const [finished, setFinished] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    history.push(queryObject.fromOnboarding ? '/home' : '/products');
  };

  const handleContinue = useCallback(() => {
    if (
      selectedInstagramMedia &&
      currentImageIndex < selectedInstagramMedia.length - 1
    ) {
      scrollTop('auto');
      setCurrentImageIndex(prev => prev + 1);
    } else {
      setFinished(true);
    }
    resetProductState();
  }, [currentImageIndex, resetProductState, selectedInstagramMedia]);

  useEffect(() => {
    if (createProductSuccess) {
      setProductsCreated(prev => prev + 1);
      setDefaultImages([]);
      handleContinue();
    }
  }, [createProductSuccess, handleContinue]);

  const [earlyExitModal, setEarlyExitModal] = useState(false);
  const handleEarlyExitModalConfirm = () => {
    setFinished(true);
  };

  useEffect(() => {
    if (finished) {
      if (productsCreated > 0) setSuccessModalOpen(true);
      else history.push(queryObject.fromOnboarding ? '/home' : '/products');
    }
  }, [finished, history, productsCreated, queryObject.fromOnboarding]);

  return (
    <div className={classes.root}>
      {/* <SnackBar
        mode="success"
        message={createProductSuccess ? 'Producto Creado' : ''}
      /> */}
      <SnackBar
        mode="error"
        message={instagramError ? `Error de Instagram: ${instagramError}` : ''}
      />
      <AlertDueDateTier />
      {!selectedInstagramMedia || !selectedInstagramMedia[currentImageIndex] ? (
        // {false ? (
        <>
          <div className={classes.headerWrapper}>
            <Typography className={classes.title}>
              <IconButton
                className={classes.backButton}
                onClick={() => history.goBack()}
              >
                <ArrowBack />
              </IconButton>
              {`${
                user ? user.firstName : ''
              }, ¡Está súper tu feed de Instagram!`}
            </Typography>
          </div>
          {/* {(isSaving || isFetching || compressing) && <LoadingSpinner />} */}

          <main className={classes.content}>
            <div className={classes.contentSection}>
              <div className={classes.textRow}>
                <Typography className={classes.descriptionText}>
                  Elige las fotos que desees subir a tu tienda virtual. Cada
                  foto será un producto diferente.
                </Typography>
                <Hidden xsDown>
                  <Typography className={classes.selectedCountText}>
                    {`(${selectedInstagramMedia.length} foto${
                      selectedInstagramMedia.length !== 1 ? 's' : ''
                    } seleccionadas)`}
                  </Typography>
                </Hidden>
              </div>
              <div className={classes.instagramImagesContainer}>
                {!!instagramMedia && !!instagramMedia.length
                  ? instagramMedia.map(
                      (image: InstagramImageType, i: number) =>
                        (image.media_type === 'IMAGE' ||
                          image.media_type === 'CAROUSEL_ALBUM') && (
                          <InstagramImage
                            key={`insta-img-${i}`}
                            image={image}
                            selected={
                              selectedInstagramMedia.findIndex(
                                selectedImage =>
                                  selectedImage && selectedImage.id === image.id
                              ) > -1
                            }
                            isAlbum={image.media_type === 'CAROUSEL_ALBUM'}
                            handleImageSelect={handleImageSelect}
                          />
                        )
                    )
                  : !instagramLoading && (
                      <Typography style={{margin: '32px auto'}}>
                        La cuenta de instagram no contiene imágenes válidas
                      </Typography>
                    )}
              </div>
              {instagramLoading && (
                <CircularProgress style={{alignSelf: 'center', margin: 16}} />
              )}
              {!!instagramPaging && !!instagramPaging.next && (
                <CustomButton
                  className={classes.loadMoreButton}
                  onClick={loadMore}
                >
                  Cargar más
                </CustomButton>
              )}
            </div>
          </main>
          <Hidden xsDown>
            <div className={classes.bottomBar}>
              <div className={classes.bottomBarContent}>
                <Typography className={classes.bottomBarText}>
                  {`(${selectedInstagramMedia.length} foto${
                    selectedInstagramMedia.length !== 1 ? 's' : ''
                  } seleccionadas)`}
                </Typography>
                <CustomButton
                  variant="contained"
                  className={classes.bottomBarButton}
                  onClick={handleContinue}
                  disabled={
                    !selectedInstagramMedia || !selectedInstagramMedia.length
                  }
                >
                  Continuar
                </CustomButton>
              </div>
            </div>
          </Hidden>
          <Hidden smUp>
            <div className={classes.bottomBar}>
              <Typography className={classes.selectedCountText}>
                {`${selectedInstagramMedia.length} foto${
                  selectedInstagramMedia.length !== 1 ? 's' : ''
                } seleccionadas`}
              </Typography>
              <CustomButton
                variant="contained"
                className={classes.mobileBottomButton}
                onClick={handleContinue}
                disabled={
                  !selectedInstagramMedia || !selectedInstagramMedia.length
                }
              >
                Continuar
              </CustomButton>
            </div>
          </Hidden>
        </>
      ) : (
        <>
          <CustomExitPrompt
            when={!finished}
            title="¿Quieres salir?"
            description="Los productos se perderán y tendrás que volver a seleccionarlos desde tu Instagram."
          />
          <CustomModal
            open={earlyExitModal}
            classes={{
              root: classes.rootModal,
            }}
            handleCloseModal={() => setEarlyExitModal(false)}
          >
            <div className={classes.earlyExitModal}>
              <Typography className={classes.earlyExitModalTitle}>
                ¿Quieres salir?
              </Typography>
              <Typography className={classes.earlyExitModalText}>
                Los productos se perderán y tendrás que volver a seleccionarlos
                desde tu Instagram.
              </Typography>
              <CustomButton
                variant="contained"
                className={classes.earlyExitModalConfirmButton}
                onClick={handleEarlyExitModalConfirm}
              >
                Si salir
              </CustomButton>
              <CustomButton
                variant="outlined"
                className={classes.earlyExitModalCancelButton}
                onClick={() => {
                  setEarlyExitModal(false);
                }}
              >
                No, completarla ahora
              </CustomButton>
            </div>
          </CustomModal>
          <div className={classes.headerWrapper}>
            <Typography className={classes.title}>
              {/* <IconButton
                className={classes.backButton}
                onClick={() => setCurrentImageIndex(-1)}
              >
                <ArrowBack />
              </IconButton> */}
              ¡Listo! Ahora a completar el detalle de tu producto
            </Typography>
          </div>
          <Typography className={classes.subTitle}>
            Completa los datos de tu producto
          </Typography>
          <div className={classes.progressBackground}>
            <LinearProgress
              variant="determinate"
              classes={{
                root: classes.progressBarRoot,
                colorPrimary: classes.progressBarColorPrimary,
                bar: classes.progressBarBar,
              }}
              value={
                ((currentImageIndex + 1) / selectedInstagramMedia.length) * 100
              }
            />
            <Typography className={classes.progressBarText}>
              {`${currentImageIndex + 1}/${
                selectedInstagramMedia.length
              } productos`}
            </Typography>
          </div>

          {!!selectedInstagramMedia[currentImageIndex] &&
            !!defaultImages &&
            !!defaultImages.length && (
              // $FlowFixMe
              <Product
                key={currentImageIndex}
                defaultImages={defaultImages}
                defaultDescription={
                  selectedInstagramMedia[currentImageIndex].caption
                }
                isInstagramUpload
                instagramUploadEarlyExit={() => setEarlyExitModal(true)}
              />
            )}
          <CustomModal
            open={successModalOpen}
            handleCloseModal={handleCloseSuccessModal}
          >
            <div className={classes.successModal}>
              <img src={imagesConfirm} alt="" />
              <Typography className={classes.successModalText}>{`Se ha${
                productsCreated !== 1 ? 'n' : ''
              } creado ${productsCreated} producto${
                productsCreated !== 1 ? 's' : ''
              } nuevo${productsCreated !== 1 ? 's' : ''}`}</Typography>
              <CustomButton
                variant="contained"
                className={classes.successModalButton}
                onClick={handleCloseSuccessModal}
              >
                {queryObject.fromOnboarding
                  ? 'Confirmar'
                  : 'Ir a ver productos'}
              </CustomButton>
            </div>
          </CustomModal>
        </>
      )}
    </div>
  );
}

InstagramProductsUpload.propTypes = {
  store: PropTypes.object,
  user: PropTypes.object,
  isSaving: PropTypes.bool,
  createProductSuccess: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  resetProductState: PropTypes.func,
};

export default connect(
  (state: RootState) => ({
    user: state.auth.user,
    store: state.store.store,
    isSaving: state.products.isSaving,
    createProductSuccess: state.products.createProductSuccess,
  }),
  (dispatch: Dispatch<any>) =>
    bindActionCreators(
      {
        resetProductState: ProductsActions.resetProductState,
      },
      dispatch
    )
)(InstagramProductsUploadPage);
